import React, { useState, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { SwipeableDrawer as Swipeable, Tooltip } from "@mui/material";
import { Pagination } from "@material-ui/lab";
import { MdFilterList, MdOutlineFilterListOff, MdPeople } from "react-icons/md";
import { useExportarControleVisitas } from "services/api/Common_ControleVisitas/listar-controle-visitas";
import NavCommon from "../../components/@navHeader";
import InputGrid from "../leads/components/InputGrid";
import DataTable from "../../components/DataTable";
import FiltroVisitas from "./components/FiltroVisitas";
import ModalDetalheVisita from "./components/ModalDetalheVisita";
import * as IconsSVG from "../../assets/iconsSVG";
import color from "../../constants/color";
import materialUI from "../../material-UI";
import {
  useListarControleVisitas,
  FiltrosControleVisitas,
} from "../../services/api/Common_ControleVisitas";
import { useDebounce } from "../../hooks/useDebounce";

const ControleVisitas: React.FC = () => {
  const history = useHistory();
  const [searchValue, setSearchValue] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>(null);
  const [filterKey, setFilterKey] = useState(0);
  const [paginationParams, setPaginationParams] = useState({
    paginaAtual: 1,
    totalItensRetorno: 100,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedVisita, setSelectedVisita] = useState<any>(null);
  const [showDetalheModal, setShowDetalheModal] = useState(false);

  // Debounce da busca para evitar chamadas excessivas à API (500ms)
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // Construir objeto de filtros para API
  const buildFilters = (): FiltrosControleVisitas | undefined => {
    if (!appliedFilters && !debouncedSearchValue) return undefined;

    const filters: FiltrosControleVisitas = {};

    // Palavra-chave da busca (usando valor debounced)
    if (debouncedSearchValue) {
      filters.palavraChave = debouncedSearchValue;
    }

    // Filtros aplicados
    if (appliedFilters) {
      if (appliedFilters.empreendimento?.length > 0) {
        filters.idsEmpreendimento = appliedFilters.empreendimento;
      }
      if (appliedFilters.corretorOrigem?.length > 0) {
        filters.idsCorretorOrigem = appliedFilters.corretorOrigem;
      }
      if (appliedFilters.tipoAtendimento?.length > 0) {
        filters.idsTipoAtendimento = appliedFilters.tipoAtendimento;
      }
      if (appliedFilters.origem?.length > 0) {
        filters.idsOrigem = appliedFilters.origem;
      }
      if (appliedFilters.canalContato?.length > 0) {
        filters.idsCanal = appliedFilters.canalContato;
      }
      if (appliedFilters.dataVisitaInicio) {
        filters.dataInicioVisita = appliedFilters.dataVisitaInicio;
      }
      if (appliedFilters.dataVisitaFim) {
        filters.dataFimVisita = appliedFilters.dataVisitaFim;
      }

      // Precedência: corretor de origem > equipe > superintendente
      if (!appliedFilters.corretorOrigem?.length) {
        const idsEquipe =
          appliedFilters.equipe?.length > 0
            ? appliedFilters.equipe
            : appliedFilters.superintendente || [];
        if (idsEquipe.length > 0) {
          filters.idsEquipe = idsEquipe;
        }
      }
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  // Hook para buscar dados da API
  // O TanStack Query faz refetch automático quando a queryKey muda (paginaAtual, totalItensRetorno ou filtros)
  const {
    data: controleVisitasData,
    isLoading,
    isFetching,
    refetch: refetchControleVisitas,
  } = useListarControleVisitas({
    queryKey: "listar-controle-visitas",
    paginaAtual: paginationParams.paginaAtual,
    totalItensRetorno: paginationParams.totalItensRetorno,
    filtros: buildFilters(),
    enabled: true,
  });

  // Forçar refetch quando os filtros aplicados mudam
  useEffect(() => {
    if (appliedFilters) {
      refetchControleVisitas();
    }
  }, [appliedFilters]);

  // Atualizar totais quando os dados mudarem
  useEffect(() => {
    if (controleVisitasData?.pagination) {
      setTotalItems(controleVisitasData.pagination.totalItems || 0);
      setTotalPages(controleVisitasData.pagination.quantityPages || 1);
    } else if (controleVisitasData?.data) {
      setTotalItems(controleVisitasData.data.length);
      setTotalPages(
        Math.ceil(
          controleVisitasData.data.length / paginationParams.totalItensRetorno
        )
      );
    }
  }, [controleVisitasData, paginationParams.totalItensRetorno]);

  // Mapear dados da API para o formato da tabela
  // Filtros são aplicados server-side via buildFilters()
  const filteredVisitas = useMemo(() => {
    const visitas = controleVisitasData?.data || [];
    const normalizeDisplayValue = (value?: string | null) =>
      value && value.trim() && value.trim() !== "." ? value : "-";

    return visitas.map((item) => ({
      id: item.id,
      lead: item.idLeadCorretor365 || "-",
      dataVisita: item.dataHoraVisita,
      tipoAtendimento: item.tipoAtendimento,
      nome: item.nomeLead,
      empreendimento: item.empreendimento,
      email: normalizeDisplayValue(item.email),
      telefone: normalizeDisplayValue(item.telefone),
      corretorOrigem: normalizeDisplayValue(item.corretorOrigem),
      corretorAtendimento: normalizeDisplayValue(item.corretor),
      origem: normalizeDisplayValue(item.origem),
      canalContato: normalizeDisplayValue(item.canalOrigem),
      cep: normalizeDisplayValue(item.cep),
      bairro: normalizeDisplayValue(item.bairro),
      bairroResidencial: normalizeDisplayValue(item.bairro),
      bairroComercial: normalizeDisplayValue(item.bairroComercial),
      idLeadCorretor: item.idLeadCorretor365 || "-",
      empresaVendas: item.empresaVendas,
      localVenda: item.localVenda,
      registradoPor: item.registradoPor,
      podeVisualizar: item.podeVisualizar ?? true,
    }));
  }, [controleVisitasData]);

  const handleSearch = () => {
    // Lógica de busca já está sendo feita via useMemo
  };

  const handleOpenFilters = () => {
    setShowFilters(true);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
  };

  const handleApplyFilters = (filters: any) => {
    setAppliedFilters(filters);
    setFiltersApplied(true);
    setPaginationParams((prev) => ({
      ...prev,
      paginaAtual: 1,
    }));
  };

  const handleClearFilters = () => {
    setFiltersApplied(false);
    setAppliedFilters(null);
    setSearchValue("");
    setShowFilters(false);
    setFilterKey((prev) => prev + 1); // Força remount do componente de filtros
    // Resetar paginação causa refetch automático via TanStack Query
    setPaginationParams({
      paginaAtual: 1,
      totalItensRetorno: 100,
    });
  };

  const handleRemoveFilters = () => {
    handleClearFilters();
  };

  const exportMutation = useExportarControleVisitas({
    filtros: buildFilters(),
  });

  const handleExport = () => {
    exportMutation.mutate();
  };

  const handlePagination = (_e: any, _page: number) => {
    setPaginationParams((prev) => ({
      ...prev,
      paginaAtual: _page,
    }));
  };

  const handleItemsPerPageChange = (event: any) => {
    setPaginationParams({
      totalItensRetorno: event.target.value,
      paginaAtual: 1, // Reset para primeira página ao mudar itens por página
    });
  };

  const countAppliedFilters = useMemo(() => {
    if (!appliedFilters) return 0;
    let count = 0;

    // Contar filtros de data
    if (appliedFilters.dataVisitaInicio || appliedFilters.dataVisitaFim) {
      count += 1;
    }

    // Contar filtros simples
    if (appliedFilters.origem && appliedFilters.origem.length > 0) count += 1;
    if (appliedFilters.canalContato && appliedFilters.canalContato.length > 0)
      count += 1;

    // Contar filtros de array
    if (
      appliedFilters.tipoAtendimento &&
      appliedFilters.tipoAtendimento.length > 0
    )
      count += 1;
    if (
      appliedFilters.empreendimento &&
      appliedFilters.empreendimento.length > 0
    )
      count += 1;
    if (
      appliedFilters.corretorAtendimento &&
      appliedFilters.corretorAtendimento.length > 0
    )
      count += 1;
    if (
      appliedFilters.corretorOrigem &&
      appliedFilters.corretorOrigem.length > 0
    ) {
      count += 1;
    } else if (appliedFilters.equipe && appliedFilters.equipe.length > 0) {
      count += 1;
    } else if (
      appliedFilters.superintendente &&
      appliedFilters.superintendente.length > 0
    ) {
      count += 1;
    }

    return count;
  }, [appliedFilters]);

  const columns = [
    { key: "lead", label: "Lead", align: "left" as const },
    { key: "dataVisita", label: "Data da Visita", align: "left" as const },
    {
      key: "tipoAtendimento",
      label: "Tipo de Atendimento",
      align: "left" as const,
    },
    { key: "nome", label: "Nome", align: "left" as const },
    {
      key: "empreendimento",
      label: "Empreendimento",
      align: "left" as const,
    },
    { key: "email", label: "E-mail", align: "left" as const },
    { key: "telefone", label: "Telefone", align: "left" as const },
    {
      key: "corretorOrigem",
      label: "Corretor de Origem",
      align: "left" as const,
    },
    {
      key: "corretorAtendimento",
      label: "Corretor Atendimento",
      align: "left" as const,
    },
  ];

  return (
    <>
      <NavCommon
        breadcrumbs={[
          { path: "/controle-visitas", name: "Controle de Visitas" },
        ]}
        onBack={() => {
          history.goBack();
        }}
        onChangeActionNew={() => {}}
      />
      <Content>
        <TitleContainer>
          <Title>Controle de Visitas</Title>
        </TitleContainer>
        <TableContainer>
          <Header>
            <Tooltip
              followCursor
              title={
                exportMutation.isLoading
                  ? "Exportando..."
                  : "Exportar para Excel"
              }
              placement="top"
              arrow
            >
              <ExportButton
                onClick={handleExport}
                style={{
                  opacity: exportMutation.isLoading ? 0.6 : 1,
                  pointerEvents: exportMutation.isLoading ? "none" : "auto",
                }}
              >
                <IconsSVG.ExportarExcelIcon />
              </ExportButton>
            </Tooltip>
            <SearchContainer>
              <InputGrid
                style={{ width: "400px" }}
                variant="outlined"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setPaginationParams((prev) => ({
                    ...prev,
                    paginaAtual: 1,
                  }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                handleBtnSearch={handleSearch}
              />
              <FilterButton type="button" onClick={handleOpenFilters}>
                <MdFilterList size={20} />
              </FilterButton>
              <RemoveFilterButton
                type="button"
                onClick={handleRemoveFilters}
                disabled={!filtersApplied && !searchValue.trim()}
              >
                <MdOutlineFilterListOff size={20} />
                {countAppliedFilters > 0 && (
                  <FilterBadge>{countAppliedFilters}</FilterBadge>
                )}
              </RemoveFilterButton>
            </SearchContainer>
          </Header>
          <TableWrapper>
            {isLoading || isFetching ? (
              <LoadingContainer>
                <p>Carregando visitas...</p>
              </LoadingContainer>
            ) : (
              <DataTable
                columns={columns}
                data={filteredVisitas}
                renderCell={(column, row) => {
                  if (column.key === "lead") {
                    const canNavigate =
                      row.podeVisualizar &&
                      row.idLeadCorretor &&
                      row.idLeadCorretor !== "-";
                    return (
                      <LeadIconContainer
                        onClick={() => {
                          if (canNavigate) {
                            history.push(`/lead-detail/${row.idLeadCorretor}`);
                          }
                        }}
                        style={{
                          cursor: canNavigate ? "pointer" : "default",
                          opacity: row.podeVisualizar ? 1 : 0.4,
                        }}
                      >
                        <MdPeople
                          size={18}
                          color={row.podeVisualizar ? "#2d5bff" : "#999"}
                        />
                      </LeadIconContainer>
                    );
                  }
                  if (column.key === "nome") {
                    return (
                      <NameLink
                        onClick={() => {
                          setSelectedVisita(row);
                          setShowDetalheModal(true);
                        }}
                      >
                        {row.nome}
                      </NameLink>
                    );
                  }
                  return row[column.key] || "-";
                }}
                emptyMessage="Nenhuma visita encontrada"
              />
            )}
          </TableWrapper>
          <ContainerPagination>
            <Arrows>
              <Pagination
                color="standard"
                count={totalPages}
                page={paginationParams.paginaAtual}
                onChange={handlePagination}
                showFirstButton
                size="small"
                variant="outlined"
                shape="round"
                showLastButton
              />
            </Arrows>
            <ShowTotalText>
              Exibindo{" "}
              {paginationParams.totalItensRetorno < totalItems
                ? paginationParams.totalItensRetorno
                : totalItems}{" "}
              visita(s) de {totalItems}
            </ShowTotalText>
            <Arrows>
              <small style={{ fontFamily: "Inter" }}>Itens por página</small>
              <Select
                displayEmpty
                inputProps={{ "aria-label": "Without label" }}
                color="secondary"
                variant="outlined"
                labelId="pages"
                id="pages"
                value={paginationParams.totalItensRetorno || 10}
                label="Pages"
                onChange={handleItemsPerPageChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </Arrows>
          </ContainerPagination>
        </TableContainer>
      </Content>
      <SwipeableDrawer
        open={showFilters}
        anchor="right"
        onClose={handleCloseFilters}
        onOpen={handleOpenFilters}
      >
        <FiltroVisitas
          key={filterKey}
          onClose={handleCloseFilters}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          appliedFilters={appliedFilters}
          isOpen={showFilters}
        />
      </SwipeableDrawer>
      <ModalDetalheVisita
        open={showDetalheModal}
        onClose={() => {
          setShowDetalheModal(false);
          setSelectedVisita(null);
        }}
        visita={selectedVisita}
      />
    </>
  );
};

export default ControleVisitas;

const Content = styled.div`
  padding: 30px;
  margin-top: 100px;
  width: 100%;
  background-color: #f5f5f5;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ExportButton = styled.div`
  height: 36px;
  min-width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  border: 1px solid #e0e0e0;
  width: 36px;
  position: relative;
  cursor: pointer;

  &:hover {
    background-color: ${color.ice};
  }
`;

const Title = styled.h1`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 29px;
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;
  margin: 0;
  color: #00000099;
`;

const TableContainer = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: calc(100vh - 250px);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const FilterButton = styled.button`
  background: #2d5bff;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: background-color 0.2s;

  &:hover {
    background: #1e4ed8;
  }
`;

const RemoveFilterButton = styled.button`
  background: ${(props) => (props.disabled ? "#e5e7eb" : "#2d5bff")};
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: background-color 0.2s;
  position: relative;

  &:hover:not(:disabled) {
    background: #1e4ed8;
  }
`;

const FilterBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  border: 2px solid white;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const LeadIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  font-family: "Inter";
  color: #46505a;
  font-size: 14px;
`;

const NameLink = styled.span`
  color: #2d5bff;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SwipeableDrawer = styled(Swipeable)`
  .MuiDrawer-paperAnchorRight {
    width: 392px;
    border-radius: 8px 0px 0px 8px;
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: #2d5bff;
      border-radius: 8px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #2d5bff;
    }
    scrollbar-width: thin;
    scrollbar-color: #2d5bff transparent;
  }
`;

const ContainerPagination = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 30px;
  margin-left: 0px;
  margin-bottom: 20px;
  margin-right: 0px;
`;

const Arrows = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  li {
    button {
      background: #f2f4f8;
      color: #819dff;
      border: none;
      width: 26px;
      height: 26px;
    }
    .Mui-selected {
      background-color: #819dff;
      color: #ffffff;
      border-radius: 3px;
    }
  }
`;

const ShowTotalText = styled.p`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${color.primary};
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const Select = styled(materialUI.Select)`
  padding-left: 4px;
  margin-left: 10px;
  color: #46505a;
  border: 1px solid #c6ccd7;
  border-radius: 16px;
  height: 32px;

  legend {
    span {
      display: none;
    }
  }
`;

const MenuItem = styled(materialUI.MenuItem)`
  font-family: Inter;
  font-size: 14px;
`;
